import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Button, IconButton, OutlinedInput, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux";
import AlertDialog from "../../../../ui/components/alert-dialog/alert-dialog";
import AlertMessage from "../../../../ui/components/alert-message/alert-message";
import AutocompleteSelect from "../../../../ui/components/autocomplete-select/autocomplete-select";
import DateInputFilter from "../../../../ui/components/date-input-filter/date-input-filter";
import Table from "../../../../ui/components/table/table";
import { UserInfo } from "../../../../ui/models/userInfo";
import { ListModel } from "../../../models/list-model";
import { PracticaList } from "../../../models/practica-list";
import { practicaServices } from "../../../services/practica.services";
import { profesionalServices } from "../../../services/profesional.services";
import {
  getFormattedDate,
  getFormattedDecimal,
} from "../../../utils/common.utils";
import {
  ListaEstados,
  formatearEstado,
  initialFilter,
  queryParamsConfig,
} from "../../../utils/practica.utils";
import { initialProfesionalFilter } from "../../../utils/profesional.utils";
import { useQueryParamsReplace } from "../../../utils/useQueryParamsReplace";
import PracticaAddModal from "../practica-add-modal/practica-add-modal";
import PracticaEditModal from "../practica-edit-modal/practica-edit-modal";
import "./practicas-list.scss";

export default function PracticasList() {
  const [queryParams, setQueryParams] =
    useQueryParamsReplace(queryParamsConfig);
  const [fetching, setFetching] = useState<boolean>(true);
  const [entities, setEntities] = useState<PracticaList[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { state, search } = location;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [textAlertModal, setTextAlertModal] = useState<string>("");
  const [practicaSelected, setPracticaSelected] = useState<PracticaList>();
  const [profesionalIdSelected, setProfesionalIdSelected] = useState<string>();
  const [profesionales, setProfesionales] = useState<ListModel[]>([]);
  const [estados, setEstados] = useState<ListModel[]>([]);
  const [queryParamsProfesional, setQueryParamsProfesional] = useState<any>();
  const [monthSelected, setMonthSelected] = useState<number>();
  const [yearSelected, setYearSelected] = useState<number>();

  const getProfesionales = async (searchValue: string) => {
    setQueryParamsProfesional({
      ...initialProfesionalFilter,
      filter: searchValue,
    });
    profesionalServices
      .lookup(queryParamsProfesional)
      .then((response) => {
        const items: ListModel[] = [];
        response.data.forEach((element: any) => {
          items.push({
            value: element.id,
            label: element.value + " " + element.description,
          });
        });
        setProfesionales(items);
      })
      .catch((error) => {});
  };

  const authInfo = useSelector<RootState, UserInfo>(
    ({ auth }: any) => auth.info,
    shallowEqual
  );

  useEffect(() => {
    setEstados(ListaEstados);
  }, []);

  useEffect(() => {
    if (location.search === "") {
      setQueryParams(initialFilter);
    }
    if (queryParams.page && authInfo) {
      getPracticas();
    }
    if (authInfo) {
      setColumnsToShow();
    }
  }, [queryParams, authInfo]);

  useEffect(() => {
    getPracticas();
  }, [profesionalIdSelected, monthSelected, yearSelected]);

  useEffect(() => {
    if (state?.textAlert) setShowAlertMessage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const setColumnsToShow = () => {
    if (authInfo.role === "ADMIN") {
      const newColumn = {
        name: "Profesional",
        selector: (row: any) => row.profesionalDescripcion,
        sortable: true,
        sortField: "profesional",
      };
      setColumns([newColumn, ...initialColumns]);
    } else setColumns(initialColumns);
  };

  useEffect(() => {
    if (authInfo) {
      if (authInfo.role === "ADMIN") {
        getProfesionales("");
      }
    }
  }, [authInfo]);

  const getPracticas = async () => {
    if (authInfo) {
      let params = { ...queryParams };
      if (
        (authInfo.role === "PROFESIONAL" ||
          authInfo.role === "PROFESIONALWEB") &&
        authInfo.profesionalId
      ) {
        params = { ...params, profesionalId: authInfo.profesionalId };
      } else if (authInfo.role === "ADMIN" && profesionalIdSelected) {
        params = { ...params, profesionalId: profesionalIdSelected };
      }
      if (yearSelected && monthSelected !== undefined) {
        console.log("llega");
        let fecha = new Date(yearSelected, monthSelected, 1);
        fecha.setUTCHours(0, 0, 0, 0);
        const fechaISO = fecha.toISOString();
        params = {
          ...params,
          mesAno: fechaISO,
        };
        console.log(fechaISO);
      }
      setFetching(true);
      practicaServices
        .getList(params)
        .then((response) => {
          setFetching(false);
          setEntities(response.data.items);
          setTotalCount(response.data.totalItems);
        })
        .catch((error) => {});
    }
  };

  const [columns, setColumns] = useState<TableColumn<PracticaList>[]>([]);

  const initialColumns: TableColumn<PracticaList>[] = [
    {
      name: "Obra Social",
      selector: (row) => row.obraSocialNombre,
      sortable: true,
      sortField: "obrasocial",
    },
    {
      name: "Fecha",
      selector: (row) => getFormattedDate(row.fecha.toString()),
      sortable: true,
      sortField: "fecha",
    },
    {
      name: "Paciente",
      selector: (row) => row.paciente,
      sortable: true,
      sortField: "paciente",
    },
    {
      name: "Nro. Validación Online",
      selector: (row) => row.nroValidacionOnline,
    },
    {
      name: "Código de Práctica",
      selector: (row) => row.practicaCodigo,
      wrap: true,
    },
    {
      name: "Descripcion",
      selector: (row) => row.practicaDescripcion,
    },
    {
      name: "Bono",
      selector: (row) => row.bono,
    },
    {
      name: "Cantidad",
      selector: (row) => row.cantidad,
    },
    {
      name: "Valor",
      selector: (row) => getFormattedDecimal(row.precio),
    },
    {
      name: "Valor Total",
      selector: (row) => getFormattedDecimal(row.precio * row.cantidad),
      wrap: true,
    },
    {
      name: "Observacion",
      selector: (row) => row.observacion,
      wrap: true,
    },
    {
      name: "Estado",
      selector: (row) => formatearEstado(row.estado),
      wrap: true,
    },
    {
      name: "",
      button: true,
      cell: (row) => (
        <>
          {row.estado === "PENDIENTE" && (
            <Tooltip title="Modificar">
              <IconButton
                aria-label="modificar"
                onClick={() => handleShowEditModal(row)}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          {row.estado === "PENDIENTE" && (
            <Tooltip title="Eliminar">
              <IconButton
                aria-label="delete"
                onClick={() => handleDeleteRow(row)}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
      width: "5%",
      style: {
        position: "sticky",
        right: 0,
        backgroundColor: "#fff",
        zIndex: 1,
      },
    },
  ];

  const handleDeleteRow = (practica: PracticaList) => {
    setShowAlertModal(true);
    setPracticaSelected(practica);
    setTextAlertModal(
      `Está por eliminar la práctica ${practica.practicaDescripcion} del paciente ${practica.paciente} del día ${getFormattedDate(practica.fecha.toString())}. ¿Desea continuar?`
    );
  };

  const handleShowAddModal = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = (added: boolean, item: any) => {
    setShowAddModal(false);
    if (added) {
      getPracticas();
      navigate(
        {
          pathname: ".",
          search,
        },
        {
          state: {
            typeAlert: "success",
            textAlert: `Se agregó la práctica del paciente ${item.paciente}.`,
          },
        }
      );
    }
  };

  const deletePractica = () => {
    if (practicaSelected) {
      setFetching(true);
      practicaServices
        .delete(practicaSelected.id)
        .then((response) => {
          setShowAlertModal(false);
          getPracticas();
          navigate(
            {
              pathname: ".",
              search,
            },
            {
              state: {
                typeAlert: "success",
                textAlert: `Se eliminó la práctica del paciente ${response.data?.paciente}.`,
              },
            }
          );
        })
        .catch((error) => {});
    }
  };

  const handleShowEditModal = (practica: PracticaList) => {
    setShowEditModal(true);
    setPracticaSelected(practica);
  };

  const handleCloseEditModal = (added: boolean, item: any) => {
    setShowEditModal(false);
    if (added) {
      getPracticas();
      navigate(
        {
          pathname: ".",
          search,
        },
        {
          state: {
            typeAlert: "success",
            textAlert: `Se modificó la práctica del paciente ${item.paciente}.`,
          },
        }
      );
    }
  };

  const refetch = () => {
    getPracticas();
  };

  const ActionsComponent = () => {
    return (
      <>
        <Button
          className="close-button"
          onClick={(e) => {
            setShowAlertModal(false);
            setPracticaSelected(undefined);
          }}
        >
          Cancelar
        </Button>
        <Button
          className="create-button"
          variant="contained"
          onClick={() => deletePractica()}
        >
          Eliminar
        </Button>
      </>
    );
  };

  return (
    <div className="practicas-list-container">
      <AlertMessage
        text={state?.textAlert}
        type={state?.typeAlert}
        handleShowExternaly={true}
        show={showAlertMessage}
        setShow={setShowAlertMessage}
      />
      <div className="row action-button">
        <Button
          className="button"
          variant="contained"
          onClick={() => handleShowAddModal()}
        >
          Nueva Práctica
        </Button>
        <DateInputFilter
          name="mesAno"
          hiddenDay={true}
          onChange={(value: Dayjs) => {
            if (value !== null) {
              setMonthSelected(value.get("month"));
              setYearSelected(value.get("year"));
            } else {
              setMonthSelected(undefined);
              setYearSelected(undefined);
            }
          }}
          maxDate={dayjs()}
        />
        <OutlinedInput
          type={"text"}
          placeholder="Afiliado, nro. de validación online y nro. de bono"
          className={`custom-input-search`}
          autoComplete="off"
          onChange={(event) => {
            setQueryParams({
              ...queryParams,
              search:
                event.target.value !== "" ? event.target.value : undefined,
            });
          }}
        ></OutlinedInput>
        <AutocompleteSelect
          name="estado"
          label="Estado"
          isMultiple={false}
          options={estados}
          onChange={(value) => {
            setQueryParams({
              ...queryParams,
              estado: value !== "" ? value : undefined,
            });
          }}
        ></AutocompleteSelect>
        {/* {authInfo && authInfo.role === "ADMIN" && profesionales.length > 0 && (
          <AutocompleteSelect
            name="profesionalId"
            label="Profesional"
            isMultiple={false}
            options={profesionales}
            onChange={(value) => {
              setProfesionalIdSelected(value);
            }}
          ></AutocompleteSelect>
        )} */}
      </div>
      {authInfo && authInfo.role === "ADMIN" && profesionales.length > 0 && (
        <div className="row action-button">
          <AutocompleteSelect
            name="profesionalId"
            label="Profesional"
            isMultiple={false}
            options={profesionales}
            onChange={(value) => {
              setProfesionalIdSelected(value);
            }}
          ></AutocompleteSelect>
        </div>
      )}
      <div className="row">
        <div className="practicas-table">
          <Table
            table={{
              columns,
              data: entities,
              paginationTotalRows: totalCount,
              pointerOnHover: true,
              highlightOnHover: true,
              // onRowClicked: handleView,
              // noDataComponent: !fetching && noDataComponent,
            }}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
          ></Table>
        </div>
      </div>
      {!!showAddModal && (
        <PracticaAddModal
          show={showAddModal}
          handleClose={handleCloseAddModal}
          refetch={refetch}
        />
      )}
      {!!showAlertModal && (
        <AlertDialog
          title={"Advertencia"}
          text={textAlertModal}
          show={showAlertModal}
          setShow={setShowAlertModal}
          handleShowExternaly={true}
          actions={<ActionsComponent />}
        />
      )}
      {!!showEditModal && (
        <PracticaEditModal
          show={showEditModal}
          practicaId={practicaSelected ? practicaSelected?.id : ""}
          handleClose={handleCloseEditModal}
          refetch={refetch}
        />
      )}
    </div>
  );
}
